import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import resourceService from '../services/resourceService';
import resourceEnrollmentService from '../services/resourceEnrollmentService';
import { useAuth } from '../contexts/AuthContext';

const NotesPage = () => {
  const [notes, setNotes] = useState([]);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [enrolledNotesIds, setEnrolledNotesIds] = useState(new Set());
  
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotesAndEnrollments();
  }, [isAuthenticated]);

  useEffect(() => {
    if (selectedSubject) {
      setFilteredNotes(notes.filter(n => n.subject === selectedSubject));
    } else {
      setFilteredNotes(notes);
    }
  }, [selectedSubject, notes]);

  const fetchNotesAndEnrollments = async () => {
    try {
      setLoading(true);
      const [notesData, enrollmentsData] = await Promise.all([
        resourceService.getResourcesByType('NOTES'),
        isAuthenticated ? resourceEnrollmentService.getMyResourceEnrollments() : Promise.resolve([])
      ]);
      const notesList = Array.isArray(notesData) ? notesData : [];
      setNotes(notesList);
      setFilteredNotes(notesList);

      const uniqueSubjects = [...new Set(notesList.map(n => n.subject).filter(Boolean))].sort();
      setSubjects(uniqueSubjects);

      if (enrollmentsData && enrollmentsData.length > 0) {
        const ids = new Set(enrollmentsData.map(e => e.resource.id));
        setEnrolledNotesIds(ids);
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load notes.');
    } finally {
      setLoading(false);
    }
  };

  const handleCardClick = (id) => {
    navigate(`/notes/${id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-3 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-50 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
             <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-slate-100 border border-slate-200 mb-4">
              <span className="text-xs font-medium text-slate-700">Study Materials</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-heading font-bold text-slate-900 mb-4 tracking-tight">
              Study Notes
            </h1>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive notes to boost your preparation
            </p>
          </div>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {error && <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">{error}</div>}

          <div className="mb-8">
            <select
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
              className="block w-full max-w-xs px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent bg-white text-slate-700"
            >
              <option value="">All Subjects</option>
              {subjects.map((subject) => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredNotes.map((note) => {
              const isEnrolled = enrolledNotesIds.has(note.id);
              return (
                <div 
                  key={note.id} 
                  className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-all duration-300 flex flex-col cursor-pointer"
                  onClick={() => handleCardClick(note.id)}
                >
                  <div className="relative overflow-hidden h-48 bg-slate-100">
                    <img
                      src={note.thumbnailUrl || 'https://placehold.co/400x250/e2e8f0/475569?text=Notes'}
                      alt={note.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    {note.subject && (
                      <div className="absolute top-3 right-3 bg-slate-900/90 text-white px-3 py-1 rounded-md text-xs font-medium backdrop-blur-sm">
                        {note.subject}
                      </div>
                    )}
                  </div>

                  <div className="p-6 flex-1 flex flex-col">
                    <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2 line-clamp-2 group-hover:text-slate-700 transition-colors">
                      {note.title}
                    </h3>
                    
                    <p className="text-sm text-slate-600 mb-4 line-clamp-2 flex-1">
                      {note.description}
                    </p>

                    <div className="flex items-center justify-between pt-4 border-t border-slate-200 mt-auto">
                      <div className="flex items-center gap-2">
                        {note.price == 0 && <span className="text-xl font-bold text-green-500">Free</span>}
                        {note.price != 0 &&<span className="text-xl font-bold text-slate-900">₹{note.price}</span>}
                      </div>

                      <div className={`inline-flex items-center justify-center px-4 py-2 text-xs font-medium rounded-lg transition-all duration-200 ${
                          isEnrolled 
                              ? 'text-white bg-slate-900 hover:bg-slate-800' 
                              : 'text-slate-700 bg-white border border-slate-200 hover:bg-slate-50'
                      }`}>
                          {isEnrolled ? 'Open PDF' : 'View Details'}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredNotes.length === 0 && !loading && (
             <div className="text-center py-12 text-slate-500">No notes found.</div>
          )}
        </div>
      </section>
    </div>
  );
};

export default NotesPage;